import { useEffect } from "react"
import { UserRoundX } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@f2e/ui/components/base"
import { ellipsis } from "@f2e/shared/utils"

import { PermissionButton } from "@/components/Auth"
import { useToast } from "@/hooks"
import {
  useRemoveWorkspaceMembersApi,
  RemoveWorkspaceMembersRequest,
} from "@/hooks/api"
import { useStore } from "@/store"
import { Workspace, Member, Permission, WorkspaceRole } from "@/types"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

export interface MemberActionButtonProps {
  className?: string
  workspace: Workspace
  member: Member
  onLeave?: (member: Member) => void
}

const MemberActionButton = ({
  className,
  workspace,
  member,
  onLeave,
}: MemberActionButtonProps) => {
  const { alertToast, successToast } = useToast()
  const { t } = useTranslation()
  const [currentOrganization] = useStore((state) => [state.currentOrganization])

  // APIs
  const {
    error: removeMembersError,
    data: removeMembersResp,
    fetchFn: removeMembersFn,
  } = useRemoveWorkspaceMembersApi(currentOrganization!.id, workspace.id)

  const isOwner = workspace.owner.id === member.id

  useEffect(() => {
    if (!removeMembersError) return
    console.error("Failed to remove members from workspace", removeMembersError)

    alertToast({
      title: "Failed to update workspace",
      description: getUserFacingApiErrorMessage(
        removeMembersError,
        t,
        removeMembersResp?.message || "Failed to update workspace"
      ),
    })
  }, [removeMembersError, removeMembersResp, alertToast, t])

  const handleRemoveMember = () => {
    const reqData: RemoveWorkspaceMembersRequest = {
      user_ids: [member.id],
    }

    removeMembersFn({
      jsonBody: reqData,
      callbackFn: (resp) => {
        if (!resp || !resp.data) return

        successToast({
          title: "User removed from workspace",
          description: `User [${ellipsis(member.name, 54)}] removed successfully`,
        })
        onLeave?.(member)
      },
    })
  }

  return (
    <div className={className}>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <PermissionButton
            permission={
              member.role === WorkspaceRole.Admin
                ? Permission.WsRemoveAdmins
                : Permission.WsRemoveMembers
            }
            workspace={workspace}
            variant="ghost"
            size="icon"
            disabled={isOwner}
          >
            <UserRoundX className="h-4 w-4" />
          </PermissionButton>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Are you sure you want to remove this user?
            </AlertDialogTitle>
            <AlertDialogDescription>
              User "{ellipsis(member.name, 54) + ` (${member.email})`}" will be
              removed from the workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export { MemberActionButton }
export default MemberActionButton
